import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Mail, Send, User } from 'lucide-react';
import { showSuccess, showError } from '../utils/alerts';
import { transactionService } from '../services/transactionService';
import { Transaction } from '../types/transaction';

interface SendEmailFormData {
  email: string;
  customMessage?: string;
}

interface SendQuotationEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotation: Transaction | null;
}

const emailValidationSchema = yup.object({
  email: yup.string()
    .email('Email inválido')
    .required('El email es requerido'),
  customMessage: yup.string()
    .max(500, 'El mensaje no puede exceder 500 caracteres')
});

const SendQuotationEmailModal: React.FC<SendQuotationEmailModalProps> = ({
  isOpen,
  onClose,
  quotation
}) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue
  } = useForm<SendEmailFormData>({
    resolver: yupResolver(emailValidationSchema),
    defaultValues: {
      email: '',
      customMessage: ''
    }
  });

  // Set customer email when quotation changes
  React.useEffect(() => {
    if (quotation?.cliente?.email) {
      setValue('email', quotation.cliente.email);
    } else {
      setValue('email', '');
    }
  }, [quotation, setValue]);

  const sendEmailMutation = useMutation({
    mutationFn: (data: SendEmailFormData) => {
      if (!quotation) throw new Error('No hay cotización seleccionada');
      return transactionService.sendQuotationByEmail(quotation.id, {
        email: data.email,
        customMessage: data.customMessage || undefined
      });
    },
    onSuccess: () => {
      showSuccess('Cotización enviada por email exitosamente');
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      handleClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Error enviando cotización por email';
      showError(message);
    }
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: SendEmailFormData) => {
    await sendEmailMutation.mutateAsync(data);
  };

  if (!isOpen || !quotation) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Enviar Cotización por Email</h2>
              <p className="text-sm text-gray-600">Cotización {quotation.numero}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Customer Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Cliente:</span>
            </div>
            <p className="text-sm text-gray-900">{quotation.cliente?.name || 'Cliente sin nombre'}</p>
            {quotation.cliente?.email && (
              <p className="text-sm text-gray-600">Email registrado: {quotation.cliente.email}</p>
            )}
          </div>

          {/* Email Input */}
          <div>
            <label className="form-label">
              Email de destino <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              {...register('email')}
              className="form-input"
              placeholder="cliente@email.com"
            />
            {errors.email && (
              <p className="form-error">{errors.email.message}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Se enviará la cotización en formato HTML profesional
            </p>
          </div>

          {/* Custom Message */}
          <div>
            <label className="form-label">
              Mensaje personalizado (opcional)
            </label>
            <textarea
              {...register('customMessage')}
              className="form-input resize-none"
              rows={4}
              placeholder="Mensaje adicional para el cliente... (Se incluirá antes del contenido de la cotización)"
            />
            {errors.customMessage && (
              <p className="form-error">{errors.customMessage.message}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Máximo 500 caracteres
            </p>
          </div>

          {/* Email Preview Info */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">El email incluirá:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Información completa de la cotización</li>
              <li>• Detalles de productos/servicios con precios</li>
              <li>• Totales y condiciones de pago</li>
              <li>• Información de contacto de la empresa</li>
              <li>• Diseño profesional y responsivo</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="btn-outline flex-1"
              disabled={isSubmitting || sendEmailMutation.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || sendEmailMutation.isPending}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {(isSubmitting || sendEmailMutation.isPending) ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Enviar Email
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendQuotationEmailModal;